/**
 * Orik's colours, as a layer over the harness's theme.
 *
 * The harness's theme (`ui-theme`) owns the palette: it resolves light, dark or system, and the frame's presenter
 * writes the active theme's tokens on `<body>`. Orik does not restyle anything; it stacks one override layer
 * (`ctx.theme.overrideTokens`) that points the harness's tokens at Orik's palette, in both schemes. The same layer
 * carries Orik's own palette variables (`--background`, `--primary`, …), which the Tailwind classes of Orik's
 * occupants read (`src/app/orik-dsh.css`), so everything on the page switches scheme with the harness.
 *
 * The preference is the harness's own setting, kept by its settings forms in this browser's settings document
 * (`harness/orik-settings.ts`), the same document Orik's sign-in pages read. Orik only mirrors the active scheme
 * onto `<html>` for the `dark:` variant of its own pages and occupants.
 */

/** One colour, in the light and dark palettes. */
interface Modes {
  readonly light: string;
  readonly dark: string;
}

/** Orik's palette (the same values as the sign-in pages' `src/app/tailwind.css`). */
export const ORIK_PALETTE = {
  background: { light: "#fafafa", dark: "#131317" },
  foreground: { light: "#202127", dark: "#efeff3" },
  card: { light: "#ffffff", dark: "#1b1b21" },
  popover: { light: "#ffffff", dark: "#222229" },
  primary: { light: "#6552ec", dark: "#aca2f8" },
  primaryForeground: { light: "#ffffff", dark: "#1c1640" },
  secondary: { light: "#efeff2", dark: "#26262e" },
  secondaryForeground: { light: "#34343d", dark: "#e5e5ed" },
  muted: { light: "#f0f0f3", dark: "#24242c" },
  mutedForeground: { light: "#656570", dark: "#a2a2b0" },
  accent: { light: "#ececf1", dark: "#2d2b36" },
  destructive: { light: "#c12e42", dark: "#fb8394" },
  border: { light: "#e2e2e8", dark: "#34343e" },
  input: { light: "#d4d4dd", dark: "#464653" },
  ring: { light: "#8274f0", dark: "#9a8ff5" },
  sidebar: { light: "#f3f3f6", dark: "#19191f" },
  sidebarAccent: { light: "#e8e6ee", dark: "#2b2836" },
  success: { light: "#18734c", dark: "#6fd0a0" },
  warning: { light: "#946013", dark: "#e3ba72" },
} as const satisfies Record<string, Modes>;

const p = ORIK_PALETTE;
const same = (value: string): Modes => ({ light: value, dark: value });
/** A mix of two palette colours, per scheme. */
const mix = (a: Modes, share: number, b: Modes | "transparent"): Modes => ({
  light: `color-mix(in oklab, ${a.light} ${String(share)}%, ${b === "transparent" ? b : b.light})`,
  dark: `color-mix(in oklab, ${a.dark} ${String(share)}%, ${b === "transparent" ? b : b.dark})`,
});

/** The override layer: the harness's tokens on Orik's palette, and Orik's own palette variables. */
export const ORIK_TOKENS: Record<string, Modes> = {
  // Type: Geist, self-hosted (`src/app/orik-dsh.css`).
  "--dsw-font-family": same('"Geist", ui-sans-serif, system-ui, sans-serif'),
  "--ds-font-family-code": same('"Geist Mono", ui-monospace, monospace'),
  // Surfaces.
  "--dsw-alias-bg-base": p.background,
  "--dsw-alias-bg-layer-1": p.card,
  "--dsw-alias-bg-layer-2": p.popover,
  "--dsw-alias-bg-layer-3": p.popover,
  "--dsw-specific-selector": p.popover,
  "--dsw-specific-sidebar-fill": p.sidebar,
  "--dsw-specific-sidebar-nav-item-hover": mix(p.sidebarAccent, 60, p.sidebar),
  "--dsw-specific-sidebar-nav-item-active": p.sidebarAccent,
  // Raised buttons (the sidebar's New Session) sit on the sidebar as cards.
  "--dsw-alias-button-elevated-fill": p.card,
  "--dsw-alias-button-floating-fill": p.card,
  "--dsw-alias-button-floating-hover": p.accent,
  // Labels.
  "--dsw-alias-label-primary": p.foreground,
  "--dsw-alias-label-secondary": mix(p.foreground, 72, p.mutedForeground),
  "--dsw-alias-label-tertiary": p.mutedForeground,
  "--dsw-alias-label-caption": mix(p.mutedForeground, 78, p.background),
  "--dsw-alias-label-primary-inverted": p.primaryForeground,
  "--dsw-alias-label-primary-foreground": p.primaryForeground,
  // Borders.
  "--dsw-alias-border-l1": p.border,
  "--dsw-alias-border-l2": p.border,
  "--dsw-alias-border-l3": p.input,
  // Accent: Orik's violet where the harness uses its brand colour.
  "--dsw-alias-brand-primary": p.primary,
  "--dsw-alias-state-business-primary": p.primary,
  "--dsw-alias-link": p.primary,
  "--dsw-alias-button-primary-fill": p.primary,
  "--dsw-alias-button-primary-hover": mix(p.primary, 86, p.foreground),
  "--dsw-focus-ring-color": p.ring,
  // States.
  "--dsw-alias-state-error-primary": p.destructive,
  "--dsw-alias-state-success-primary": p.success,
  "--dsw-alias-state-warn-primary": p.warning,
  // Interaction.
  "--dsw-alias-interactive-bg-hover": mix(p.foreground, 6, "transparent"),
  "--dsw-alias-interactive-bg-hover-solid": p.accent,
  "--dsw-alias-interactive-bg-active": mix(p.foreground, 10, "transparent"),
  // The user's message bubble, the composer card and code.
  "--dsw-specific-bubble": p.muted,
  "--dsw-specific-input-major": p.card,
  "--dsw-alias-markdown-code-block": p.muted,
  "--dsw-alias-markdown-inline-code": p.muted,
  // Orik's own palette variables, for the Tailwind classes of Orik's occupants.
  "--background": p.background,
  "--foreground": p.foreground,
  "--card": p.card,
  "--card-foreground": p.foreground,
  "--popover": p.popover,
  "--popover-foreground": p.foreground,
  "--primary": p.primary,
  "--primary-foreground": p.primaryForeground,
  "--secondary": p.secondary,
  "--secondary-foreground": p.secondaryForeground,
  "--muted": p.muted,
  "--muted-foreground": p.mutedForeground,
  "--accent": p.accent,
  "--accent-foreground": p.foreground,
  "--destructive": p.destructive,
  "--border": p.border,
  "--input": p.input,
  "--ring": p.ring,
  "--success": p.success,
  "--warning": p.warning,
};

/** The source the layer is registered under. */
const LAYER = "@vexxhost/atlas-harness";

/** The part of the harness's theme service Orik uses. */
export interface ThemeService {
  overrideTokens(source: string, tokens: Record<string, Modes>): () => void;
}

/** The theme snapshot `theme/change` carries, as far as Orik reads it. */
interface ThemeSnapshot {
  readonly active: { readonly colorScheme: "light" | "dark" };
}

/**
 * Colour the harness with Orik's palette, and follow its scheme on `<html>`.
 *
 * @param ctx - the kernel context Orik's entry is applied to.
 */
export function installOrikTheme(ctx: { theme: ThemeService; on(event: "theme/change", listener: (snapshot: ThemeSnapshot) => void): () => void }): void {
  ctx.theme.overrideTokens(LAYER, ORIK_TOKENS);
  // The `dark:` variant of Orik's sign-in pages and occupants follows the same choice.
  ctx.on("theme/change", snapshot => {
    document.documentElement.classList.toggle("dark", snapshot.active.colorScheme === "dark");
  });
}
